import PropTypes from 'prop-types';
import { IconCheck, IconStop } from '../components/icons';
import { useTranslation } from 'react-i18next';

import CourseBackground from './CourseBackground';
import { COURSE_BACKGROUNDS } from './catalog';
import './courseBackgrounds.css';

/**
 * Grid of the predefined course backgrounds, plus a "none" option.
 *
 * A controlled `value`/`onChange` pair so it drops straight into an antd
 * `Form.Item name="backgroundKey"`. The value is the catalogue key, or null.
 */
const CourseBackgroundPicker = ({ value, onChange, disabled }) => {
  const { t } = useTranslation();

  const select = (key) => {
    if (disabled) return;
    // Clicking the selected theme again clears it, so a background is never a
    // one-way door once the admin has picked one.
    onChange?.(value === key ? null : key);
  };

  const renderOption = (key, label, swatch) => {
    const selected = (value ?? null) === key;
    return (
      <button
        key={key ?? 'none'}
        type="button"
        className={`course-bg-picker__option${selected ? ' course-bg-picker__option--selected' : ''}`}
        onClick={() => select(key)}
        disabled={disabled}
        aria-pressed={selected}
        aria-label={label}
        title={label}
      >
        {selected && (
          <span className="course-bg-picker__check">
            <IconCheck />
          </span>
        )}
        {swatch}
        <span className="course-bg-picker__label">{label}</span>
      </button>
    );
  };

  return (
    <div className="course-bg-picker" role="group">
      {renderOption(
        null,
        t('course.background.none'),
        <div className="course-bg-picker__swatch course-bg-picker__swatch--none">
          <IconStop />
        </div>,
      )}
      {COURSE_BACKGROUNDS.map((theme) =>
        renderOption(
          theme.key,
          t(`course.background.${theme.key}`),
          <CourseBackground themeKey={theme.key} className="course-bg-picker__swatch" />,
        ),
      )}
    </div>
  );
};

CourseBackgroundPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

CourseBackgroundPicker.defaultProps = {
  value: null,
  onChange: undefined,
  disabled: false,
};

export default CourseBackgroundPicker;
